/**
 * Web Push subscription helper — talks to the browser's PushManager and
 * hands the resulting subscription object to our backend.
 *
 * iter166 — introduced so BirthdayCheerCard (and any future callers)
 * can opt users into real-time notifications with a single call. The
 * flow is idempotent: repeated calls on an already-subscribed browser
 * short-circuit and just POST the existing subscription to make sure
 * the backend still has it.
 */
import { api } from "./api";

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) buf[i] = raw.charCodeAt(i);
  return buf;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Ensures the browser is subscribed to Web Push and the backend has the
 * latest subscription object. Returns `{ ok, reason }` — never throws.
 *
 *   ok=true      → subscription is live server-side.
 *   ok=false     → reason is one of "unsupported" | "denied" | "no-vapid"
 *                  | "no-sw" | "backend-error".
 */
export async function ensurePushSubscription() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const reg = await navigator.serviceWorker.ready;
    if (!reg) return { ok: false, reason: "no-sw" };
    // Fetch VAPID public key on first opt-in.
    const { data } = await api.get("/push/vapid-public-key");
    if (!data.public_key) return { ok: false, reason: "no-vapid" };
    const permission = Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
    if (permission !== "granted") return { ok: false, reason: "denied" };
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });
    }
    // Send/refresh on the backend — idempotent upsert keyed on endpoint.
    await api.post("/push/subscribe", sub.toJSON());
    return { ok: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[push] subscribe failed", e);
    return { ok: false, reason: "backend-error", error: e };
  }
}
